<template>
  <div
    class="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-16 md:py-24 overflow-hidden"
    id="customer_reviews">

    <!-- Background decorations -->
    <div
      class="absolute top-0 right-0 w-96 h-96 bg-orange-100 dark:bg-orange-900/10 rounded-full blur-3xl opacity-40 pointer-events-none">
    </div>
    <div
      class="absolute bottom-0 left-0 w-80 h-80 bg-amber-50 dark:bg-amber-900/5 rounded-full blur-3xl opacity-60 pointer-events-none">
    </div>

    <div class="max-w-5xl mx-auto px-4 relative z-10">

      <!-- Header -->
      <div class="mb-12 text-center reviews-header">
        <div class="inline-flex items-center gap-2 justify-center mb-3">
          <div class="h-px w-8 bg-orange-600"></div>
          <span class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Customer Reviews</span>
          <div class="h-px w-8 bg-orange-600"></div>
        </div>
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
          What Our Customers <span class="text-orange-600">Say</span>
        </h2>
        <p class="text-gray-500 dark:text-slate-400 max-w-md mx-auto">See why our customers love Sultan Flowers Extract
          Palace</p>
      </div>

      <!-- Reviews List -->
      <div v-if="!activeReviews.loading" class="space-y-6">
        <TransitionGroup name="review-list">
          <div v-for="(review, idx) in activeReviews.data" :key="review.id"
            class="review-card bg-white dark:bg-slate-800/80 backdrop-blur-sm rounded-3xl shadow-md p-6 border border-slate-100 dark:border-slate-700/50 hover:shadow-xl hover:shadow-orange-100/50 dark:hover:shadow-orange-900/10 transition-all duration-400 hover:-translate-y-1"
            :style="{ transitionDelay: `${idx * 0.08}s` }">
            <!-- Header row -->
            <div class="flex items-start gap-4 mb-5">
              <!-- Avatar -->
              <div
                class="avatar-circle w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-orange-400/30 flex-shrink-0">
                {{ getInitials(review.full_name) }}
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1.5">
                  <h3 class="font-bold text-gray-900 dark:text-white capitalize">{{ review.full_name }}</h3>
                  <span class="text-xs text-gray-400 dark:text-slate-500 shrink-0">{{ review.created_at }}</span>
                </div>

                <!-- Star Rating -->
                <div class="flex items-center gap-0.5 mb-1.5">
                  <svg v-for="star in 5" :key="'star-' + star" class="w-4 h-4 transition-all duration-200"
                    fill="currentColor"
                    :class="star <= review.rate ? 'text-orange-500 scale-110' : 'text-gray-200 dark:text-slate-600'"
                    viewBox="0 0 20 20">
                    <path
                      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <span class="ms-2 text-xs font-semibold text-orange-600">{{ review.rate }}/5</span>
                </div>

                <p class="font-semibold text-gray-800 dark:text-slate-200 text-sm">{{ review.title }}</p>
              </div>
            </div>

            <!-- Review Text -->
            <p class="text-gray-600 dark:text-slate-400 mb-5 leading-relaxed text-sm ps-16">{{ review.description }}</p>

            <!-- Review Images -->
            <div v-if="review.images && review.images.length" class="flex flex-wrap gap-2 ps-16 mb-4">
              <div v-for="image in review.images" :key="image.id" class="review-img-wrap relative"
                @click="openImage(image.url)">
                <img :src="image.url"
                  class="w-16 h-16 object-cover rounded-xl border-2 border-slate-100 dark:border-slate-600 cursor-pointer hover:scale-110 hover:shadow-lg hover:border-orange-300 transition-all duration-300"
                  alt="Review image" />
                <div
                  class="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-xl transition-all duration-300 flex items-center justify-center">
                  <i
                    class="uil uil-expand-arrows-alt text-white opacity-0 hover:opacity-100 text-sm transition-opacity duration-300"></i>
                </div>
              </div>
            </div>

            <!-- Replies -->
            <div v-if="review.replies && review.replies.length > 0" class="mt-4 space-y-3 ps-16">
              <div v-for="reply in review.replies" :key="reply.id"
                class="reply-card rounded-2xl p-4 border transition-all duration-300 hover:shadow-md"
                :class="reply.is_admin ? 'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/40' : 'bg-gray-50 dark:bg-slate-700/30 border-gray-200 dark:border-slate-600/30'">
                <div class="flex items-start gap-3">
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-sm"
                    :class="reply.is_admin ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-300'">
                    {{ getInitials(reply.full_name) }}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <p class="font-semibold text-sm"
                        :class="reply.is_admin ? 'text-orange-600' : 'text-gray-800 dark:text-slate-200'">
                        {{ reply.full_name }}
                      </p>
                      <span v-if="reply.is_admin"
                        class="text-xs bg-gradient-to-r from-orange-500 to-amber-500 text-white px-2 py-0.5 rounded-full font-medium">
                        Admin
                      </span>
                    </div>
                    <p class="text-gray-600 dark:text-slate-400 text-sm leading-relaxed">{{ reply.message }}</p>

                    <!-- Reply images -->
                    <div v-if="reply.images && reply.images.length" class="flex flex-wrap gap-2 mt-2">
                      <img v-for="image in reply.images" :src="image.url" :key="image.id"
                        class="w-14 h-14 object-cover rounded-lg cursor-pointer hover:scale-110 hover:shadow-md transition-all duration-300"
                        @click="openImage(image.url)" alt="Reply image" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TransitionGroup>

        <!-- Pagination -->
        <div v-if="activeReviews.total > activeReviews.limit"
          class="bg-white dark:bg-slate-800 rounded-2xl shadow-md p-6 mt-8 border border-slate-100 dark:border-slate-700/50">
          <div class="flex flex-col md:flex-row justify-between items-center gap-4">
            <span class="text-sm font-medium text-gray-600 dark:text-slate-400">
              Showing
              <span class="text-orange-600 font-bold">{{ activeReviews.from }}</span>
              to
              <span class="text-orange-600 font-bold">{{ activeReviews.to }}</span>
              of
              <span class="text-orange-600 font-bold">{{ activeReviews.total }}</span>
              reviews
            </span>

            <nav
              class="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px overflow-hidden border border-slate-200 dark:border-slate-600"
              aria-label="Pagination">
              <a v-for="(link, i) of activeReviews.links" :key="i" :disabled="!link.url" href="#"
                @click.prevent="getForPage($event, link)"
                class="paginate-btn relative inline-flex items-center px-4 py-2.5 border-e border-slate-200 dark:border-slate-600 text-sm font-medium transition-all duration-200"
                :class="[
                  link.active
                    ? 'bg-orange-600 text-white shadow-sm z-10'
                    : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:text-orange-600',
                  !link.url ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                ]" v-html="link.label"></a>
            </nav>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else class="flex flex-col items-center justify-center py-20 gap-4">
        <div class="loading-dots flex gap-2">
          <div v-for="i in 3" :key="i" class="dot w-3 h-3 rounded-full bg-orange-500"
            :style="{ animationDelay: `${i * 0.15}s` }"></div>
        </div>
        <p class="text-slate-400 text-sm">Loading reviews...</p>
      </div>
    </div>

    <!-- Image Lightbox -->
    <Transition name="lightbox">
      <div v-if="isImageOpen"
        class="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        @click.self="closeImage">
        <button @click="closeImage"
          class="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/25 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 hover:scale-110">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="currentColor"
            stroke-width="2" viewBox="0 0 24 24">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <img :src="activeImage" class="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain" />
      </div>
    </Transition>

    <!-- Reply Modal -->
    <Transition name="fade">
      <div v-if="isModalOpen"
        class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        @click.self="closeModal">
        <Transition name="popup">
          <div v-if="isModalOpen"
            class="bg-white dark:bg-slate-800 w-full max-w-md rounded-3xl p-6 shadow-2xl border border-slate-100 dark:border-slate-700">
            <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-5">Add a Reply</h2>
            <form @submit.prevent="submitReply">
              <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Full Name</label>
                <input v-model="replyForm.full_name" type="text"
                  class="w-full p-3 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none transition-all duration-200"
                  required />
              </div>
              <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Reply</label>
                <textarea v-model="replyForm.message"
                  class="w-full p-3 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 outline-none resize-none transition-all duration-200"
                  rows="4" required></textarea>
              </div>
              <div class="flex justify-end gap-3">
                <button type="button" @click="closeModal"
                  class="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all duration-200 font-medium text-sm">
                  Cancel
                </button>
                <button type="submit"
                  class="px-5 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all duration-200 font-semibold text-sm shadow-md shadow-orange-600/20 hover:-translate-y-0.5 flex items-center gap-2"
                  :disabled="loading">
                  <span class="animate-spin" v-if="loading">⟳</span>
                  {{ loading ? 'Submitting...' : 'Submit' }}
                </button>
              </div>
            </form>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from "@/store";
import { useToast } from 'primevue/usetoast';
import { useScrollReveal } from '@/composables/useScrollReveal';

const loading = ref(false)
const toast = useToast();

const activeReviews = computed(() => store.state.activeReviews || reviews.value)
const errors = ref({ full_name: [], message: [], review_id: [] })
const reviews = ref([])

const isModalOpen = ref(false)
const replyForm = ref({ full_name: '', message: '', review_id: null, url: null, imagePreview: null })

const getInitials = (name) => name?.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '??'

const isImageOpen = ref(false)
const activeImage = ref(null)

const openImage = (url) => {
  activeImage.value = url
  isImageOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeImage = () => {
  isImageOpen.value = false
  activeImage.value = null
  document.body.style.overflow = ''
}

const closeModal = () => {
  isModalOpen.value = false
  replyForm.value = { full_name: '', message: '', url: null, imagePreview: null, review_id: null }
}


const showSuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Success', detail, life: 3000 });
};

const submitReply = () => {
  loading.value = true
  store.dispatch('addReply', replyForm.value).then(() => {
    showSuccess('Reply submitted. Pending team review and will be published within 24 hours.')
  }).catch(err => {
    errors.value = err.response?.data?.errors || {}
  }).finally(() => {
    loading.value = false
    closeModal()
  })
}

const getForPage = (event, link) => {
  if (!link.url) return
  store.dispatch('getActiveReviews', { url: link.url })
}

// Scroll reveal — re-scans when reviews arrive so the cards are never left hidden
useScrollReveal('.reviews-header, .review-card', () => activeReviews.value?.data)

onMounted(() => {
  store.dispatch('getActiveReviews')
});

onUnmounted(() => {
  document.body.style.overflow = '';
});
</script>

<style>
/* Review card reveal */
.reviews-header {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.reviews-header.revealed {
  opacity: 1;
  transform: translateY(0);
}

.review-card {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.3s ease;
}

.review-card.revealed {
  opacity: 1;
  transform: translateY(0);
}

.review-card:hover {
  transform: translateY(-4px) !important;
}

/* Avatar animated gradient */
.avatar-circle {
  background-size: 200% 200%;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

/* Loading dots animation */
.dot {
  animation: dotBounce 1.2s ease-in-out infinite;
}

@keyframes dotBounce {

  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.3;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Paginate button */
.paginate-btn {
  transition: all 0.2s ease;
}

.paginate-btn:hover:not([disabled]) {
  transform: scale(1.05);
}

/* Lightbox transition */
.lightbox-enter-active {
  animation: lightboxIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.lightbox-leave-active {
  animation: lightboxOut 0.25s ease forwards;
}

@keyframes lightboxIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes lightboxOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

/* Fade overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal popup transition */
.popup-enter-active {
  animation: popupIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.popup-leave-active {
  animation: popupOut 0.25s ease forwards;
}

@keyframes popupIn {
  from {
    opacity: 0;
    transform: scale(0.85) translateY(20px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes popupOut {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
}

/* Review list group transition */
.review-list-enter-active {
  animation: reviewIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.review-list-leave-active {
  animation: reviewOut 0.3s ease forwards;
}

@keyframes reviewIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes reviewOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

/* PrimeVue custom orange button */
.custom-orange-btn .p-button {
  background-color: #ea580c !important;
  border-color: #ea580c !important;
  color: white !important;
  border-radius: 12px !important;
}

.custom-orange-btn .p-button:hover {
  background-color: #c2410c !important;
}
</style>